import React, { useRef, useEffect, useState, useContext, useCallback } from "react";

import { initialize, TYPE_MANAGE_CONTENT_BUTTON, TYPE_MANAGE_MENU_BUTTON, TYPE_CONTAINER_UNORDERED_LIST, TYPE_CONTAINER_ORDERED_LIST, TYPE_CONTAINER_NO_MARKUP, TYPE_CONTAINER_INLINE, TYPE_CONTAINER_ITEM_UNDEFINED, isContainer, isContainerItem } from "@bloomreach/spa-sdk";

/**
 * The React Context holding the current brXM Component.
 */ const BrComponentContext = React.createContext(undefined);

function BrMeta({children, meta}) {
    var _a;
    const head = useRef(null);
    const tail = useRef(null);
    useEffect((() => {
        var _a;
        if (!((_a = head.current) === null || _a === void 0 ? void 0 : _a.nextSibling) || !tail.current) {
            return undefined;
        }
        return meta === null || meta === void 0 ? void 0 : meta.render(head.current.nextSibling, tail.current);
    }), [ meta, (_a = head.current) === null || _a === void 0 ? void 0 : _a.nextSibling, tail.current ]);
    return React.createElement(React.Fragment, null, meta && meta.length > 0 && React.createElement("span", {
        style: {
            display: "none"
        },
        ref: head
    }), children, meta && meta.length > 0 && React.createElement("span", {
        style: {
            display: "none"
        },
        ref: tail
    }));
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */ function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) {
        return value instanceof P ? value : new P((function(resolve) {
            resolve(value);
        }));
    }
    return new (P || (P = Promise))((function(resolve, reject) {
        function fulfilled(value) {
            try {
                step(generator.next(value));
            } catch (e) {
                reject(e);
            }
        }
        function rejected(value) {
            try {
                step(generator["throw"](value));
            } catch (e) {
                reject(e);
            }
        }
        function step(result) {
            result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
        }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    }));
}

typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * The React Context holding the current brXM Page.
 */ const BrPageContext = React.createContext(undefined);

/**
 * @typedef {Object} BrPageProps
 * @property {Configuration} configuration The configuration of the SPA SDK.
 * @property {Object} mapping The brXM and React components mapping.
 * @property {Page | PageModel | undefined} page The pre-initialized page instance or prefetched page model.
 * Mostly this property should be used to transfer state from the server-side to the client-side.
 */
/**
 * The brXM page.
 */ function BrPage({configuration, mapping, page: initialPage, children}) {
    const [page, setPage] = useState((() => initialPage ? initialize(configuration, initialPage) : undefined));
    useEffect((() => {
        let isMounted = true;
        const initializePage = () => __awaiter(this, void 0, void 0, (function*() {
            const newPage = initialPage ? initialize(configuration, initialPage) : yield initialize(configuration);
            if (isMounted) {
                setPage(newPage);
            }
        }));
        initializePage();
        return () => {
            isMounted = false;
        };
    }), [ configuration, initialPage ]);
    useEffect((() => {
        page === null || page === void 0 ? void 0 : page.sync();
    }), [ page ]);
    return React.createElement(BrPageContext.Provider, {
        value: page
    }, React.createElement(BrMappingContext.Provider, {
        value: mapping
    }, !page && !configuration.NBRMode && null, (page || configuration.NBRMode) && React.createElement(BrNode, {
        component: page === null || page === void 0 ? void 0 : page.getComponent()
    }, children)));
}

const BrMappingContext = React.createContext({});

function BrContainerBox({page, children}) {
    return React.createElement("div", {
        className: (page === null || page === void 0 ? void 0 : page.isPreview()) ? "hst-container" : undefined
    }, React.Children.map(children, (child => React.createElement("div", {
        className: (page === null || page === void 0 ? void 0 : page.isPreview()) ? "hst-container-item" : undefined
    }, child))));
}

function BrContainerInline(props) {
    const {page, children} = props;
    return React.createElement("div", {
        className: (page === null || page === void 0 ? void 0 : page.isPreview()) ? "hst-container" : undefined
    }, React.Children.map(children, (child => React.createElement("span", {
        className: (page === null || page === void 0 ? void 0 : page.isPreview()) ? "hst-container-item" : undefined
    }, child))));
}

function BrContainerNoMarkup({children}) {
    return React.createElement(React.Fragment, null, children);
}

function BrContainerOrderedList({page, children}) {
    return React.createElement("ol", {
        className: (page === null || page === void 0 ? void 0 : page.isPreview()) ? "hst-container" : undefined
    }, React.Children.map(children, (child => React.createElement("li", {
        className: (page === null || page === void 0 ? void 0 : page.isPreview()) ? "hst-container-item" : undefined
    }, child))));
}

function BrContainerUnorderedList({page, children}) {
    return React.createElement("ul", {
        className: (page === null || page === void 0 ? void 0 : page.isPreview()) ? "hst-container" : undefined
    }, React.Children.map(children, (child => React.createElement("li", {
        className: (page === null || page === void 0 ? void 0 : page.isPreview()) ? "hst-container-item" : undefined
    }, child))));
}

function BrContainerItemUndefined({component}) {
    return React.createElement("div", null, `Component "${component === null || component === void 0 ? void 0 : component.getType()}" is not defined.`);
}

/**
 * The button component that opens for editing a content.
 */ function BrManageContentButton(props) {
    const page = useContext(BrPageContext);
    return (page === null || page === void 0 ? void 0 : page.isPreview()) ? React.createElement(BrMeta, {
        meta: page.getButton(TYPE_MANAGE_CONTENT_BUTTON, props)
    }) : null;
}

/**
 * The button component that opens a menu editor.
 */ function BrManageMenuButton({menu}) {
    const page = useContext(BrPageContext);
    return (page === null || page === void 0 ? void 0 : page.isPreview()) ? React.createElement(BrMeta, {
        meta: page.getButton(TYPE_MANAGE_MENU_BUTTON, menu)
    }) : null;
}

function BrNodeContainer(props) {
    const context = useContext(BrMappingContext);
    const {component, children} = props;
    const getMapping = () => {
        const type = component === null || component === void 0 ? void 0 : component.getType();
        if (type && type in context) {
            return context[type];
        }
        switch (type) {
          case TYPE_CONTAINER_INLINE:
            return BrContainerInline;

          case TYPE_CONTAINER_NO_MARKUP:
            return BrContainerNoMarkup;

          case TYPE_CONTAINER_ORDERED_LIST:
            return BrContainerOrderedList;

          case TYPE_CONTAINER_UNORDERED_LIST:
            return BrContainerUnorderedList;

          default:
            return BrContainerBox;
        }
    };
    const mapping = getMapping();
    const meta = component === null || component === void 0 ? void 0 : component.getMeta();
    const content = mapping ? React.createElement(mapping, props) : children;
    return React.createElement(BrMeta, {
        meta
    }, content);
}

function BrNodeContainerItem(props) {
    const context = useContext(BrMappingContext);
    const {component, page, children} = props;
    const [forcedRerenders, setForcedRerenders] = useState(0);
    const onUpdate = useCallback((() => {
        setForcedRerenders((c => c + 1));
    }), []);
    useEffect((() => {
        component === null || component === void 0 ? void 0 : component.on("update", onUpdate);
        return () => {
            component === null || component === void 0 ? void 0 : component.off("update", onUpdate);
        };
    }), [ component, onUpdate ]);
    useEffect((() => {
        if (forcedRerenders > 0) {
            page === null || page === void 0 ? void 0 : page.sync();
        }
    }), [ page, forcedRerenders ]);
    const getMapping = () => {
        var _a;
        const type = component === null || component === void 0 ? void 0 : component.getType();
        if (type && type in context) {
            return context[type];
        }
        return (_a = context[TYPE_CONTAINER_ITEM_UNDEFINED]) !== null && _a !== void 0 ? _a : BrContainerItemUndefined;
    };
    const mapping = getMapping();
    const meta = component === null || component === void 0 ? void 0 : component.getMeta();
    const content = mapping ? React.createElement(mapping, props) : children;
    return React.createElement(BrMeta, {
        meta
    }, content);
}

function BrNodeComponent(props) {
    const context = useContext(BrMappingContext);
    const {component, children} = props;
    const mapping = component && context[component.getName()];
    const meta = component === null || component === void 0 ? void 0 : component.getMeta();
    const content = mapping ? React.createElement(mapping, props) : children;
    return React.createElement(BrMeta, {
        meta
    }, content);
}

function BrNode({children, component}) {
    const context = useContext(BrPageContext);
    function renderNode() {
        if (React.Children.count(children)) {
            return React.createElement(BrMeta, {
                meta: component === null || component === void 0 ? void 0 : component.getMeta()
            }, children);
        }
        const childrenList = component === null || component === void 0 ? void 0 : component.getChildren().map(((child, index) => React.createElement(BrNode, {
            key: index,
            component: child
        })));
        if (isContainer(component)) {
            return React.createElement(BrNodeContainer, {
                component,
                page: context
            }, childrenList);
        }
        if (isContainerItem(component)) {
            return React.createElement(BrNodeContainerItem, {
                component,
                page: context
            }, childrenList);
        }
        return React.createElement(BrNodeComponent, {
            component,
            page: context
        }, childrenList);
    }
    return React.createElement(BrComponentContext.Provider, {
        value: component
    }, renderNode());
}

/**
 * The brXM component.
 */ function BrComponent({path, children}) {
    const context = useContext(BrComponentContext);
    function getComponents() {
        if (!context || Object.keys(context).length === 0) {
            return [];
        }
        if (!path) {
            return context.getChildren();
        }
        const component = context.getComponent(...path.split("/"));
        return component ? [ component ] : [];
    }
    function renderComponents() {
        return getComponents().map(((component, index) => React.createElement(BrNode, {
            key: index,
            component
        }, children)));
    }
    return React.createElement(React.Fragment, null, renderComponents());
}

export { BrComponent, BrComponentContext, BrManageContentButton, BrManageMenuButton, BrPage, BrPageContext };
//# sourceMappingURL=index.js.map
